import { NgStyle } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import {
    AbstractControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { BehaviorSubject, firstValueFrom, forkJoin } from "rxjs";
import { ColorPickerModalComponent } from "~app/common/components/color-picker/color-picker-modal/color-picker-modal.component";
import { IMapGeometry, IMapGeometryWicket } from "~app/common/components/map/map.settings";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { ToastService } from "~app/common/components/toast/toast.service";
import { CanBeDirty } from "~app/common/guards/dirty.guard";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "~app/common/validation/validationContext";
import { ValidationMessageRowComponent } from "../../../../common/validation/validation-message-row/validation-message-row.component";
import { ValidationRuleDirective } from "../../../../common/validation/validation-rule.directive";
import { DeleteDestineringsomradeCommand } from "../commands/commands";
import { DsoEvents } from "../models/dso-events";
import { DeleteDestineringsomradeResultDto, Destineringsomrade, HandelssortimentModel } from "../models/dso-models";
import { DsoEventService } from "../services/dso-event.service";
import { DsoService } from "../services/dso.service";
import { DsoCacheService } from "./dso-cache.service";
import { DsoEditTexts } from "./dso-edit-texts";

@Component({
    selector: "rask-dso-edit",
    templateUrl: "./dso-edit.component.html",
    styleUrls: ["./dso-edit.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        FormsModule,
        ReactiveFormsModule,
        ValidationMessageRowComponent,
        InputComponent,
        ValidationRuleDirective,
        MultiSelectorComponent,
        RadiobuttonGroupComponent,
        NgStyle,
        SubmitButtonComponent,
        ButtonComponent,
        TranslatePipe
    ]
})
export class DsoEditComponent implements OnInit, OnDestroy, CanBeDirty {
    readonly dirtyFormService = inject(DirtyFormService);

    aktivRadiobuttons: IRadiobuttonItem[] = [
        {
            key: true,
            value: "Ja"
        },
        {
            key: false,
            value: "Nej"
        }
    ];
    color = "";
    colors = [];
    current: Destineringsomrade;
    handelssortiment: BehaviorSubject<HandelssortimentModel[]> = new BehaviorSubject([]);
    form = new UntypedFormGroup({
        id: new UntypedFormControl(),
        handelssortiment: new UntypedFormControl([]),
        mottagningsplatser: new UntypedFormControl([]),
        namn: new UntypedFormControl(),
        aktiv: new UntypedFormControl(true),
        farg: new UntypedFormControl(),
        geometri: new UntypedFormControl(),
        guid: new UntypedFormControl()
    }, { validators: (g) => this.onFormChanged(g) });
    texts = DsoEditTexts.texts;
    validationContext: IValidationContext;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly cacheService = inject(DsoCacheService);
    private readonly commandServiceShared = inject(CommandService);
    private readonly dsoEvents: IBridgeEvent[] = [];
    private readonly dsoService = inject(DsoService);
    private readonly eventService = inject(DsoEventService);
    private readonly ngbModal = inject(NgbModal);
    private readonly raskModalService = inject(RaskModalService);
    private readonly toastService = inject(ToastService);

    private commandService: CommandService;

    cancel() {
        this.cancelEdit();
    }

    clear() {
        this.clearForm();
    }

    delete() {
        this.deleteOmraden([this.getCurrentOmrade()], true).then(() => {
            // Exekvera promise
            this.eventService.emit(DsoEvents.dsoRemoveOutline, null);
        });
    }

    handelssortimentSearchFn = (val: string, item: HandelssortimentModel) => (item.namn?.toUpperCase().includes(val?.toUpperCase())
        || item.beteckning?.toUpperCase().includes(val?.toUpperCase()))
        ?? false;

    handelssortimentSelectedFormatter = (item) => item.id ? item.id : item;

    handelssortimentViewFormatter = (item) => `(${item.beteckning}) ${item.namn}`;

    hasErrors() {
        return this.validationContext.hasErrors(ValidationScopes.spara);
    }

    initEventListeners() {
        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.dsoSelected, (omrade: Destineringsomrade) => {
            this.current = omrade;
            if (ObjectUtils.hasValue(this.current)) {
                this.setForm(this.current, false);
            } else {
                this.clearForm();
            }
            this.validationContext.validate();
        }));

        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.geometryNewCreated, (payload: IMapGeometryWicket) => {
            if (this.form.pristine) {
                const omrade = this.cacheService.create(payload?.wkt);
                this.cacheService.setSelected(omrade.guid);
                this.setForm(omrade, true);
            }
        }));

        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.geometryModified, (payload: IMapGeometryWicket) => {
            if (this.form.get("guid").value !== payload.id) {
                console.error(`selected omrade ${this.form.get("guid").value} is different from modified geometry ${payload.id}`);
                return;
            }
            this.form.get("geometri").setValue(payload.wkt);
            this.setDirty();
        }));

        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.geometriesDeleted, (payload: IMapGeometry[]) => {
            const omradenToDelete = this.cacheService.getMany(payload.map(x => x.id));
            this.deleteOmraden(omradenToDelete, false).then(() => {
                // Exekvera promise
            });
        }));
    }

    initHandelssortiment() {
        this.dsoService.getHandelssortiment().subscribe(res => {
            this.handelssortiment.next(res);
        });
    }

    isDirty() {
        return this.form.dirty;
    }

    isDirtyFn(): Promise<boolean> {
        return this.canDeactivate();
    }

    isDisabled() {
        return ObjectUtils.isNullOrUndefined(this.form.get("guid").value) || !this.authenticationService.canEdit();
    }

    isNewUnsaved() {
        const omrade = this.cacheService.get(this.form.get("guid").value);
        return omrade?.isNew;
    }

    ngOnDestroy(): void {
        this.eventService.unsubscribeMany(this.dsoEvents);
        this.dsoEvents.splice(0);
    }

    ngOnInit(): void {
        this.commandService = this.commandServiceShared.createInstance();
        this.validationContext = this.initValidation(this.form);
        this.loadColors();
        this.initHandelssortiment();
        this.initEventListeners();
    }

    onFormChanged(g: AbstractControl): ValidationErrors | null {
        setTimeout(() => {
            this.signalFormState();
        }, 20);
        return null;
    }

    openColorPicker() {
        if (!this.authenticationService.canEdit()) {
            return;
        }
        const modalRef = this.ngbModal.open(ColorPickerModalComponent, {
            size: "sm",
            backdrop: "static"
        });
        modalRef.componentInstance.color = this.color;
        modalRef.componentInstance.colors = this.colors;
        modalRef.result.then(color => {
            if (color) {
                this.color = color; // default color
                this.form.get("farg").setValue(this.color);
                this.setDirty();
                this.signalPropertiesChanged();
            }
        });
    }

    save() {
        this.validationContext.validate();
        if (this.hasErrors()) {
            return;
        }
        const destineringsomrade = this.getFromForm();
        this.cacheService.save(destineringsomrade, (ok) => {
            if (ok) {
                this.toastService.add("Ändringarna sparades");
                this.setPristine();
                this.signalPropertiesChanged();
                this.signalFormState();
                this.eventService.emit(DsoEvents.dsoRemoveOutline, null);
            }
        });
    }

    searchForMottagningsplatsTypeAhead = (searchString: string) => this.dsoService.searchForMottagningsplatsTypeAhead(searchString, null);

    private readonly canDeactivate = (): Promise<boolean> => new Promise(resolve => {
        if (this.isDirty()) {
            const saveFn = !this.isDirty() ? (async () => {
                this.save();
                resolve(true);
            }) : null;
            return this.dirtyFormService.dirtyFormModal(async () => {
                await this.commandService.reset();
                resolve(true);
            }, saveFn, (arg) => {
                resolve(false);
            });
        } else {
            resolve(true);
            return undefined;
        }
    });

    private async cancelEdit() {
        const omrade = this.cacheService.get(this.form.get("guid").value);
        const result = await firstValueFrom(this.raskModalService.confirm(new ModalSettings("Avbryt", `Du kommer att förlora dina ändringar om du avbryter. Vill du avbryta?`)));
        if (result === ConfirmDialogResponse.save) {
            const rolledback = this.cacheService.rollback(omrade.guid);
            if (rolledback) {
                this.cacheService.setSelected(rolledback.guid);
            } else {
                this.cacheService.setSelected(null);
            }
            this.validationContext.validate();
            this.eventService.emit(DsoEvents.dsoRemoveOutline, null);
        }
    }

    private clearForm() {
        this.color = this.colors[0];
        this.form.get("id").setValue(null);
        this.form.get("handelssortiment").setValue([]);
        this.form.get("mottagningsplatser").setValue([]);
        this.form.get("namn").setValue("");
        this.form.get("aktiv").setValue(true);
        this.form.get("farg").setValue(this.color);
        this.form.get("geometri").setValue("");
        this.form.get("guid").setValue(null);
        this.form.markAsPristine();
    }

    private async deleteOmraden(destineringsOmraden: Destineringsomrade[], signal = false): Promise<void> {
        const settings = new ModalSettings(this.texts.deletePromtTitle,  `${this.texts.deletePromptText}`);
        settings.yesCaption = this.texts.deleteJa;
        settings.noCaption = this.texts.deleteNej;
        const result = await firstValueFrom(this.raskModalService.confirm(settings));

        if (result === ConfirmDialogResponse.save) {
            const commands = destineringsOmraden.map(dso => (this.dsoService.deleteDestineringsomrade(new DeleteDestineringsomradeCommand(dso.guid))));
            forkJoin(commands).subscribe((results: DeleteDestineringsomradeResultDto[]) => {
                const deleted = results.filter(x => x.result === true);
                const notDeleted = results.filter(x => x.result === false);
                const toast = deleted?.length > 1 ?
                    "Destineringsområden borttagna." :
                    // eslint-disable-next-line sonarjs/no-nested-conditional
                    (deleted?.length > 0 ? "Destineringsområdet borttaget." : "Inget område borttaget.");
                const toastFail = notDeleted?.map(dto => dto.text).join(", ");
                this.toastService.add(`${toast} ${toastFail}`, 3);
                this.omradenDeleted(deleted?.map(x => x.guid), signal);
            });
        }
    }

    private getCurrentOmrade(): Destineringsomrade {
        return this.cacheService.get(this.form.get("guid").value);
    }

    private getFromForm(): Destineringsomrade {
        const omrade = this.cacheService.get(this.form.get("guid").value);
        omrade.id = this.form.get("id").value ?? null;
        omrade.guid = this.form.get("guid").value;
        omrade.namn = this.form.get("namn").value;
        omrade.aktiv = this.form.get("aktiv").value;
        omrade.color = this.form.get("farg").value;
        omrade.handelssortiment = this.form.get("handelssortiment").value?.map(hs => ({
            id: hs.id,
            namn: hs.namn
        }));
        omrade.mottagningsplatser = this.form.get("mottagningsplatser").value?.map(mp => ({
            id: mp.id,
            namn: mp.namn
        }));
        omrade.geometri = this.form.get("geometri").value;
        return omrade;
    }

    private initValidation(form: UntypedFormGroup): ValidationContext {
        const context = new ValidationContext();
        context.create(ValidationScopes.spara).withRules([
            {
                errorLevel: ValidationErrorLevels.error,
                id: 1,
                message: { text: this.texts.validationNameRequired },
                model: () => form.get("namn").value,
                name: "namn",
                required: true,
                triggerOnLoad: false
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: 2,
                expression: (m) => this.testName(m),
                message: { text: this.texts.validationNameExists },
                model: () => form.get("namn").value,
                name: "namn",
                triggerOnLoad: false
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: 3,
                expression: (m) => m?.length <= 50,
                message: { text: this.texts.validationNameTooLong },
                model: () => form.get("namn").value,
                name: "namn",
                triggerOnLoad: false
            }
        ]);
        context.errorLevel = ValidationErrorLevels.error;
        return context;
    }

    private loadColors() {
        this.dsoService.getColors().subscribe(colors => {
            this.colors = colors;
            if (colors.length > 0 && this.color === "") {
                this.color = colors[0];
                this.form.get("farg").setValue(this.color);
            }
        });
    }

    private omradenDeleted(guids: string[], signal = false) {
        const currentGuid = this.getCurrentOmrade()?.guid;
        this.cacheService.delete(guids);
        if (guids.some(g => g === currentGuid)) {
            this.clearForm();
        }
        if (signal) {
            this.eventService.emit(DsoEvents.destineringsomradenDeleted, guids);
        }
    }

    private setDirty() {
        this.form.markAsDirty();
        this.eventService.emit(DsoEvents.destineringsomradeFormStateChanged, true);
    }

    private setForm(omrade: Destineringsomrade, dirty = null) {
        if (!ObjectUtils.hasValue(omrade)) {
            return;
        }

        this.form.get("id").setValue(omrade.id);
        this.form.get("namn").setValue(omrade.namn);
        this.form.get("aktiv").setValue(omrade.aktiv);
        this.form.get("farg").setValue(omrade.color);
        this.form.get("geometri").setValue(omrade.geometri);
        this.form.get("guid").setValue(omrade.guid);
        const selected = this.handelssortiment.value.filter(x => omrade.handelssortiment.some(y => y.id === x.id));
        this.form.get("handelssortiment").setValue(selected);
        this.form.get("mottagningsplatser").setValue(omrade.mottagningsplatser);
        this.color = omrade.color;
        this.eventService.emit(DsoEvents.destineringmoradeChanged, omrade);
        if (dirty != null) {
            if (dirty === true) {
                this.setDirty();
            } else if (dirty === false) {
                this.setPristine();
            }
        }
    }

    private setPristine() {
        this.form.markAsPristine();
        this.eventService.emit(DsoEvents.destineringsomradeFormStateChanged, false);
    }

    private signalFormState() {
        this.eventService?.emit(DsoEvents.destineringsomradeFormStateChanged, this.form.dirty);
    }

    private signalPropertiesChanged() {
        this.eventService.emit(DsoEvents.destineringsomradePropertiesChanged,
            {
                guid: this.form.get("guid").value,
                color: this.form.get("farg").value,
                name: this.form.get("namn").value
            });
    }

    private testName(name: string) {
        const guid = this.form.get("guid").value;
        return !this.cacheService.nameExists(name, guid);
    }
}
